/**
 * The sequence of one "Accept" on an access request (app#467).
 *
 * It was in the store (`useAccessGrants.approve`), which imports the Supabase client and
 * `callTool`, so `npm test` could not run it: deleting a line of it kept the suite green. Here it
 * takes everything it touches as arguments, and `approveRequest.test.ts` runs every path.
 */
import {
  afterApproveFailure,
  approveArgs,
  waitsForRootAnswer,
  type RootApproval,
} from './routerWrites.ts'

/** The fields of a request row that an answer changes. */
export type RequestAnswer = { id: string; grantedAt: string | null; rejectedAt: string | null }

export type ApproveDeps<G extends RequestAnswer & { serviceId: string }> = {
  /** The request as the store holds it NOW, or undefined when it left the store. */
  live: (id: string) => G | undefined
  /** Writes an answer on the request's row. */
  update: (answer: RequestAnswer) => void
  /** `callTool('service_share', args)`. Rejects with the router's refusal. */
  call: (args: Record<string, unknown>) => Promise<unknown>
  /** The request whose root dialog is open, or null. */
  openRootId: () => string | null
  /** Opens the root dialog for this request. */
  ask: (root: RootApproval) => void
  /** Shows one sentence on the page. Null clears it. `cause` is the router's refusal, for the console. */
  say: (message: string | null, cause?: unknown) => void
  /** Reads the stored requests again. */
  refetch: () => void
  /** The time of the answer, as an ISO string. */
  now: () => string
}

/**
 * Runs one approve and reports each step through `deps`.
 *
 * ⚠⚠ ON A ROOT TERMINAL THE FIRST CALL IS REFUSED, AND THAT IS NOT AN ERROR (app#461). Naming a
 * requester who does not control the node gives a root shell (docs/PERMISSIONS.md § 5 Services,
 * D-40), so the router asks for `confirm_root`. The refusal opens the dialog (`ask`), and the
 * dialog calls again with `confirmedRoot`.
 *
 * The rules, each with a test:
 * - The row shows "granted" BEFORE the call, and goes back when the router refuses. ⚠ Not on the
 *   first call of a root terminal (`waitsForRootAnswer`): that call is expected to be refused, and
 *   the row must not leave the list and come back behind the dialog (app#463). If the router
 *   accepts it (the requester already controls the node), the row shows "granted" after the call.
 * - The row to go back to is the row AS IT IS WHEN THE CALL STARTS, not as it was when Accept was
 *   pressed: the dialog can stay open for minutes, and realtime may bring another controller's
 *   answer in that time.
 * - A refusal that is not the root question is one sentence, and the stored rows are read again:
 *   another controller's answer can arrive during the call, and realtime does not send it twice.
 */
export async function approveRequest<G extends RequestAnswer & { serviceId: string }>(
  grant: G,
  confirmedRoot: boolean,
  service: { admin?: boolean; url?: string | null } | undefined,
  deps: ApproveDeps<G>
): Promise<void> {
  const before = deps.live(grant.id) ?? grant
  const granted = (): RequestAnswer => ({ id: grant.id, grantedAt: deps.now(), rejectedAt: null })
  const shown = !waitsForRootAnswer(service, confirmedRoot)
  if (shown) deps.update(granted())
  deps.say(null)
  try {
    await deps.call(approveArgs(grant, confirmedRoot))
    if (!shown) deps.update(granted())
  } catch (e) {
    if (shown) deps.update({ id: grant.id, grantedAt: before.grantedAt, rejectedAt: before.rejectedAt })
    const next = afterApproveFailure(e, confirmedRoot, grant.id, deps.openRootId())
    if ('ask' in next) {
      deps.ask(next.ask)
      return
    }
    deps.say(next.error, e)
    deps.refetch()
  }
}
